import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import { playNewMessageNotificationInWidget } from 'widget/helpers/WidgetAudioNotificationHelper';
import { ON_AGENT_MESSAGE_RECEIVED } from '../constants/widgetBusEvents';
import { IFrameHelper } from 'widget/helpers/utils';
import { shouldTriggerMessageUpdateEvent } from './IframeEventHelper';
import { CHATWOOT_ON_MESSAGE } from '../constants/sdkEvents';

const isMessageInActiveConversation = (getters, message) => {
  const { conversation_id: conversationId } = message;
  const activeConversationId =
    getters['conversationAttributes/getConversationParams'].id;
  return activeConversationId && conversationId !== activeConversationId;
};

const replaceResolvedMessageContent = (message, app) => {
  // Only show "You ended the chat" when customer resolves the conversation
  // Don't show "The support agent ended the chat" message to customers
  if (message.includes('was resolved by')) {
    return app.$i18n.t('ACTIVITY.CUSTOMER_END_CHAT');
  }

  // Return empty string for agent-resolved messages
  // The UI will handle not rendering empty content
  return '';
};

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    super(app, pubsubToken);
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.status_changed': this.onStatusChange,
      'conversation.created': this.onConversationCreated,
      'presence.update': this.onPresenceUpdate,
      'contact.merged': this.onContactMerge,
      'cart.updated': this.onCartUpdated,
    };
  }

  onDisconnected = () => {
    this.setLastMessageId();
  };

  onReconnect = () => {
    this.syncLatestMessages();
  };

  setLastMessageId = () => {
    this.app.$store.dispatch('conversation/setLastMessageId');
  };

  syncLatestMessages = () => {
    this.app.$store.dispatch('conversation/syncLatestMessages');
  };

  onStatusChange = data => {
    if (data.status === 'resolved') {
      this.app.$store.dispatch('campaign/resetCampaign');
    }
    this.app.$store.dispatch('conversationAttributes/update', data);
  };

  onMessageCreated = async data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    if (data.sender_type === 'AgentBot') {
      await this.app.$store.dispatch('conversation/toggleAgentTyping', {
        status: 'on',
      });

      await new Promise(resolve => {
        setTimeout(
          resolve,
          parseInt(window.chatwootWebChannel.botTypingDelay, 10)
        );
      });

      await this.app.$store.dispatch('conversation/toggleAgentTyping', {
        status: 'off',
      });
    }

    const finalData = { ...data };
    // When the message is an activity message, we need to replace the contact name with "You"
    // if the message content is like 'Conversation was resolved by <contact_name>'
    if (data.message_type === 2) {
      // Don't show status change activity messages in widget at all
      // Check the activity_type in additional_attributes for reliable filtering
      const activityType = finalData.additional_attributes?.activity_type;
      if (
        activityType &&
        (activityType === 'status_resolved' ||
          activityType === 'status_open' ||
          activityType === 'status_pending' ||
          activityType === 'status_snoozed')
      ) {
        return;
      }

      let newContent = replaceResolvedMessageContent(
        finalData.content,
        this.app
      );

      finalData.content = newContent;
      finalData.processed_message_content = newContent;

      this.app.$store.dispatch('conversation/addOrUpdateMessage', finalData);
    } else {
      this.app.$store
        .dispatch('conversation/addOrUpdateMessage', finalData)
        .then(() => window.bus.$emit(ON_AGENT_MESSAGE_RECEIVED));
    }

    IFrameHelper.sendMessage({
      event: 'onEvent',
      eventIdentifier: CHATWOOT_ON_MESSAGE,
      data: finalData,
    });
    if (data.sender_type === 'User') {
      playNewMessageNotificationInWidget();
    }
  };

  onMessageUpdated = data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    if (data.message_type === 2) {
      // Don't show status change activity messages in widget at all
      // Check the activity_type in additional_attributes for reliable filtering
      const activityType = data.additional_attributes?.activity_type;
      if (
        activityType &&
        (activityType === 'status_resolved' ||
          activityType === 'status_open' ||
          activityType === 'status_pending' ||
          activityType === 'status_snoozed')
      ) {
        return;
      }

      const newContent = replaceResolvedMessageContent(data.content, this.app);
      data.content = newContent;
      data.processed_message_content = newContent;
    }

    if (shouldTriggerMessageUpdateEvent(data)) {
      IFrameHelper.sendMessage({
        event: 'onEvent',
        eventIdentifier: CHATWOOT_ON_MESSAGE,
        data,
      });
    }

    this.app.$store.dispatch('conversation/addOrUpdateMessage', data);
  };

  onConversationCreated = () => {
    this.app.$store.dispatch('conversationAttributes/getAttributes');
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('agent/updatePresence', data.users);
  };

  // eslint-disable-next-line class-methods-use-this
  onContactMerge = data => {
    const { pubsub_token: pubsubToken } = data;
    ActionCableConnector.refreshConnector(pubsubToken);
  };

  onTypingOn = data => {
    const activeConversationId =
      this.app.$store.getters['conversationAttributes/getConversationParams']
        .id;
    const isUserTypingOnAnotherConversation =
      data.conversation && data.conversation.id !== activeConversationId;

    if (isUserTypingOnAnotherConversation || data.is_private) {
      return;
    }
    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'on',
    });
    this.initTimer();
  };

  onTypingOff = () => {
    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'off',
    });
  };

  clearTimer = () => {
    if (this.CancelTyping) {
      clearTimeout(this.CancelTyping);
      this.CancelTyping = null;
    }
  };

  initTimer = () => {
    // Turn off typing automatically after 30 seconds
    this.CancelTyping = setTimeout(() => {
      this.onTypingOff();
    }, 30000);
  };

  onCartUpdated = data => {
    if (window.CART_SYNC_DEBUG) {
      console.log('🛒 Cart Updated Event Received:', data);
    }
    // Emit an event that the cartSync mixin can listen to
    window.bus.$emit('cart:updated', data);
  };
}

export default ActionCableConnector;
